import React, { useEffect, useState } from "react";
import { UserPlus, Users, Plus, KeyRound, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { formatError, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ConfirmDelete, InlineEdit } from "@/components/AdminControls";
import { InviteLinks } from "@/components/InviteLinks";
import { BetaCodes } from "@/components/BetaCodes";
import { EmailSettings } from "@/components/EmailSettings";

function GroupChecks({ groups, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-3">
      {groups.map((g) => (
        <label key={g.id} className="flex items-center gap-1.5 text-xs">
          <Checkbox checked={value.includes(g.id)} onCheckedChange={(v) => onChange(v ? [...value, g.id] : value.filter((x) => x !== g.id))} data-testid={`${testId}-${g.id}`} />
          {g.name}
        </label>
      ))}
    </div>
  );
}

function InviteForm({ groups, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "playtester", workgroup_ids: [] });
  const [result, setResult] = useState(null);
  const submit = async () => {
    try {
      const u = await api.createUser(form);
      setResult({ ok: true, text: `Account created for ${u.email}. Temporary password: ${u.temp_password} — pass it on; they can change it after signing in.` });
      setForm({ email: "", name: "", role: "playtester", workgroup_ids: [] });
      onCreated();
    } catch (e) {
      setResult({ ok: false, text: formatError(e) });
    }
  };
  return (
    <Card>
      <CardContent className="p-4 space-y-3" data-testid="invite-form">
        <h3 className="font-semibold flex items-center gap-2"><UserPlus className="w-4 h-4 text-cyan-400" /> Invite a member</h3>
        <div className="grid md:grid-cols-3 gap-2">
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" data-testid="invite-role-select">
            <option value="playtester">Playtester</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <GroupChecks groups={groups} value={form.workgroup_ids} onChange={(w) => setForm({ ...form, workgroup_ids: w })} testId="invite-group" />
        <Button onClick={submit} disabled={!form.email.includes("@")} data-testid="invite-submit-btn">Create account</Button>
        {result && <p className={`text-sm ${result.ok ? "text-emerald-300" : "text-rose-300"}`} data-testid="invite-result">{result.text}</p>}
      </CardContent>
    </Card>
  );
}

function UserRow({ u, groups, onChanged, isMe }) {
  const [msg, setMsg] = useState(null);
  const update = async (patch) => {
    try {
      const r = await api.updateUser(u.id, patch);
      setMsg(r.temp_password ? `New temporary password: ${r.temp_password}` : null);
      onChanged();
    } catch (e) {
      setMsg(formatError(e));
    }
  };
  const sendReset = async () => {
    try {
      const r = await api.sendResetEmail(u.id);
      setMsg(`Reset link emailed to ${r.sent_to} (valid for one hour).`);
    } catch (e) {
      setMsg(formatError(e));
    }
  };
  const remove = async () => {
    try {
      await api.deleteUser(u.id);
      toast.success(`${u.name}'s account was deleted`);
      onChanged();
    } catch (e) {
      setMsg(formatError(e));
    }
  };
  const save = (patch) => api.updateUser(u.id, patch).then(onChanged);
  return (
    <div className="py-3 border-b border-border last:border-0 space-y-2" data-testid={`member-row-${u.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <InlineEdit value={u.name} onSave={(name) => save({ name })} className="font-medium" testId={`member-name-${u.id}`} />
        <InlineEdit value={u.email} onSave={(email) => save({ email })} className="text-xs text-muted-foreground" inputClass="w-60" testId={`member-email-${u.id}`} />
        {isMe && <Badge variant="outline" className="text-[10px]">you</Badge>}
        <select value={u.role} onChange={(e) => update({ role: e.target.value })} className="h-7 rounded border border-input bg-transparent px-1 text-xs" data-testid={`member-role-${u.id}`}>
          <option value="playtester">Playtester</option>
          <option value="admin">Admin</option>
        </select>
        {!u.active && <Badge variant="outline" className="text-rose-300">deactivated</Badge>}
        <label className="flex items-center gap-1.5 text-xs ml-auto">
          <Switch checked={u.active} onCheckedChange={(v) => update({ active: v })} data-testid={`member-active-${u.id}`} /> Active
        </label>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => update({ reset_password: true })} data-testid={`member-reset-${u.id}`}>
          <KeyRound className="w-3 h-3 mr-1" /> Temporary password
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={sendReset} data-testid={`member-send-reset-${u.id}`}>
          <Mail className="w-3 h-3 mr-1" /> Email reset link
        </Button>
        {!isMe && (
          <ConfirmDelete
            title={`Delete ${u.name}'s account?`}
            description="They won't be able to sign in any more. Their saved checks, comments and feedback stay and keep their name. This can't be undone — use the Active switch instead if you may want them back."
            onConfirm={remove}
            testId={`member-delete-${u.id}`}
          />
        )}
      </div>
      <GroupChecks groups={groups} value={u.workgroup_ids} onChange={(w) => update({ workgroup_ids: w })} testId={`member-group-${u.id}`} />
      {msg && <p className="text-xs text-amber-300" data-testid={`member-msg-${u.id}`}>{msg}</p>}
    </div>
  );
}

/** Admin: workgroups and members (invite-only accounts). */
function WorkgroupRow({ g, onChanged }) {
  const remove = async () => {
    try {
      const r = await api.deleteWorkgroup(g.id);
      toast.success(`Deleted "${g.name}" and ${r.checks_deleted} saved checks`);
      onChanged();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm py-1 border-b border-border last:border-0" data-testid={`workgroup-${g.id}`}>
      <InlineEdit value={g.name} onSave={(name) => api.renameWorkgroup(g.id, name).then(onChanged)} className="font-medium" testId={`workgroup-name-${g.id}`} />
      <Badge variant="outline" className="text-[11px]">{g.member_count} members</Badge>
      <Badge variant="outline" className="text-[11px]">{g.check_count} saved checks</Badge>
      {g.is_default ? (
        <span className="text-[11px] text-muted-foreground ml-auto">default group — can be renamed, not deleted</span>
      ) : (
        <span className="ml-auto">
          <ConfirmDelete
            title={`Delete the workgroup "${g.name}"?`}
            description={`This permanently deletes its ${g.check_count} saved checks with their comments, its wording feedback and live drafts. Members stay in their other groups (anyone left without a group moves to the main group).`}
            onConfirm={remove}
            testId={`workgroup-delete-${g.id}`}
          />
        </span>
      )}
    </div>
  );
}

export function MembersAdmin() {
  const me = useAuth()?.user;
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState("");
  const load = () => Promise.all([api.listUsers(), api.listWorkgroups()]).then(([u, g]) => { setUsers(u); setGroups(g); });
  useEffect(() => { load().catch((e) => console.warn("members load failed", e)); }, []);
  const addGroup = async () => {
    await api.createWorkgroup(newGroup.trim());
    setNewGroup("");
    load();
  };
  return (
    <div className="grid gap-4" data-testid="members-admin">
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-cyan-400" /> Workgroups</h3>
          <div className="grid gap-1.5">
            {groups.map((g) => <WorkgroupRow key={g.id} g={g} onChanged={load} />)}
          </div>
          <div className="flex gap-2">
            <Input placeholder="New workgroup name" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} className="max-w-xs" data-testid="workgroup-new-input" />
            <Button variant="outline" onClick={addGroup} disabled={!newGroup.trim()} data-testid="workgroup-create-btn"><Plus className="w-4 h-4 mr-1" /> Create</Button>
          </div>
        </CardContent>
      </Card>
      <BetaCodes groups={groups} />
      <InviteLinks groups={groups} onChanged={load} />
      <InviteForm groups={groups} onCreated={load} />
      <EmailSettings />
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-1">Members</h3>
          {users.map((u) => <UserRow key={u.id} u={u} groups={groups} onChanged={load} isMe={u.id === me?.id} />)}
        </CardContent>
      </Card>
    </div>
  );
}
